import React, { useState, useEffect, useRef } from "react";
import { object } from "prop-types";

import TextInput from "../FormItems/TextInput.jsx";
import Textarea from "../FormItems/Textarea.jsx";
import GeneralError from "../FormItems/GeneralError.jsx";

const FETCH_TIMEOUT_MS = 90000;

async function fetchWithTimeout(url, options = {}, timeoutMs = FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(new Error(`Request timed out after ${timeoutMs}ms`)),
    timeoutMs,
  );
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

// TPS GetMetaData returns: Stewart-Lookup.Agencies.Agency[].ContractedCounties.ContractedCounty[].@Name
function parseCountiesFromResponse(result) {
  if (!result || !result["Stewart-Lookup"]) return [];
  const agencies = result["Stewart-Lookup"].Agencies;
  const agencyList = agencies && agencies.Agency
    ? (Array.isArray(agencies.Agency) ? agencies.Agency : [agencies.Agency])
    : [];
  const countySet = new Set();
  agencyList.forEach(agency => {
    const cc = agency && agency.ContractedCounties && agency.ContractedCounties.ContractedCounty;
    if (!cc) return;
    const list = Array.isArray(cc) ? cc : [cc];
    list.forEach(c => {
      const name = c && c["@Name"];
      if (name) countySet.add(name);
    });
  });
  return Array.from(countySet).sort();
}

const propTypes = {
  name: object,
  email: object,
  subject: object,
  message: object,
};

const CPLForm = props => {
  // const { name, email, subject, message } = props;
  const cplFormRef = useRef(null);
  const [showSecondaryLoanLender, setShowSecondaryLoanLender] = useState(false);
  const [showSecondaryPropertyBorrower, setShowSecondaryPropertyBorrower] =
    useState(false);
  const [clientIp, setClientIp] = useState("");
  const [isMakingAPIRequest, setIsMakingAPIRequest] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [readyToFinalize, setReadyToFinalize] = useState(false); // first submit
  const [formSubmitted, setFormSubmitted] = useState(false); // final submit
  const [counties, setCounties] = useState([]);
  const [isLoadingCounties, setIsLoadingCounties] = useState(true);

  // Captured from the Preview response so the Issue request reuses the same
  // TPS ProductUniqueID. 
  const [tpsProductUniqueID, setTpsProductUniqueID] = useState(null);

  async function fetchIPAddress() {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      if (response.ok) {
        const result = await response.json();
        return result.ip || "";
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Failed to fetch IP address", err);
    }
    return "";
  }

  useEffect(() => {
    // Fetch IP once on mount.
    fetchIPAddress().then(ip => setClientIp(ip));

    // Fetch counties from TPS metadata for Property County dropdown.
    (async () => {
      try {
        const response = await fetchWithTimeout(
          "/api/TPS/GetMetaData",
          { method: "GET", headers: { "Content-Type": "application/json" } },
        );
        if (response.ok) {
          const result = await response.json();
          const parsed = parseCountiesFromResponse(result);
          if (parsed.length) setCounties(parsed);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Failed to fetch counties", err);
      } finally {
        setIsLoadingCounties(false);
      }
    })();
  }, []);

  const handlePreviewSubmit = async e => {
    e.preventDefault();

    // Get all form fields
    const formFields = e.target.querySelectorAll("input, select, textarea");
    let hasErrors = false;

    formFields.forEach(field => {
      const fieldWrapper = field.closest(".field-wrapper");
      if (!fieldWrapper) return;

      // Remove existing error class
      fieldWrapper.classList.remove("has-error");

      // Skip validation for non-required fields
      if (!field.hasAttribute("data-required")) {
        return;
      }

      // Check if field has a value
      const isEmpty = !field.value || field.value.trim() === "";

      if (isEmpty) {
        fieldWrapper.classList.add("has-error");
        hasErrors = true;
      }
    });

    // If there are errors, prevent form submission
    if (hasErrors) {
      return;
    }

    // Continue with form submission logic here
    console.log("Form is valid, proceeding with submission...");

    // Collect form data
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";

    // Add additional data
    data.userAgent = userAgent;
    data.clientIpAddress = clientIp;
    data.Action = "Preview";
    data.operation = "submitOrder";

    // Submit to API
    try {
      setIsMakingAPIRequest(true);

      const response = await fetchWithTimeout(
        "/api/TPS/SubmitCplOrder",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );

      if (response.ok) {
        const result = await response.json();
        const returnedUniqueId =
          result && typeof result.TPSProductUniqueID === "string"
            ? result.TPSProductUniqueID.trim()
            : "";
        if (
          result &&
          result.Success &&
          result.DocumentContent &&
          returnedUniqueId
        ) {
          setPdfDoc(result.DocumentContent);
          // Persist the unique ID returned by the backend so the Issue call can
          // reference the same order draft instead of creating a new one.
          setTpsProductUniqueID(returnedUniqueId);
          setReadyToFinalize(true);
        } else {
          setPdfDoc(null);
          setTpsProductUniqueID(null);
          setReadyToFinalize(false);
          // eslint-disable-next-line no-console
          console.error(
            "Preview failed:",
            (result && result.Message) ||
              (!returnedUniqueId
                ? "Backend response missing TPSProductUniqueID."
                : "Unknown error"),
          );
        }
      } else {
        setPdfDoc(null);
        setTpsProductUniqueID(null);
        setReadyToFinalize(false);
        // eslint-disable-next-line no-console
        console.error("Form submission failed:", response.statusText);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      if (error && error.name === "AbortError") {
        console.error("Preview request timed out (>90s). Backend slow or CORS preflight blocked.");
      } else if (error instanceof TypeError) {
        console.error("Preview network/CORS error — check browser network tab for blocked OPTIONS or 4xx:", error);
      } else {
        console.error("Error submitting form:", error);
      }
    } finally {
      setIsMakingAPIRequest(false);
    }
  };

  const previewPDF = () => {
    if (pdfDoc) {
      // Handle binary response (likely a PDF or document)
      const binaryData = atob(pdfDoc);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  };

  const handleFinalSubmit = async e => {
    e.preventDefault();

    if (isMakingAPIRequest) return;

    if (!tpsProductUniqueID) {
      // eslint-disable-next-line no-console
      console.error(
        "Missing TPSProductUniqueID. Please preview the document again before submitting.",
      );
      setPdfDoc(null);
      setReadyToFinalize(false);
      return;
    }

    // Site Key owned by Stewart Title
    // As of August 2025, was not configured to work with localhost requests
    // so this will never submit successfully in Storybook.
    const SITE_KEY = `6Lc8rwYaAAAAAGsJKqZhD-FjPHtuq1D56kx47AnM`;

    const { grecaptcha } = window;
    if (!grecaptcha || typeof grecaptcha.execute !== "function") {
      // eslint-disable-next-line no-console
      console.error("reCAPTCHA is not available. Ensure the script is loaded.");
      return;
    }

    setIsMakingAPIRequest(true);
    try {
      const grecaptchaToken = await grecaptcha.execute(SITE_KEY, {
        action: "submit",
      });
      if (!grecaptchaToken) {
        // eslint-disable-next-line no-console
        console.error("reCAPTCHA returned an empty token.");
        return;
      }

      const formData = new FormData(cplFormRef.current);
      const data = Object.fromEntries(formData.entries());

      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent : "";

      data.userAgent = userAgent;
      data.clientIpAddress = clientIp;
      data.Action = "Issue";
      data.operation = "submitOrder";
      data.GRecaptchaToken = grecaptchaToken;
      data.TPSProductUniqueID = tpsProductUniqueID;

      const response = await fetchWithTimeout(
        "/api/TPS/SubmitCplOrder",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );

      if (response.ok) {
        const result = await response.json();
        if (result && result.Success && result.DocumentContent) {
          setPdfDoc(result.DocumentContent);
          setFormSubmitted(true);
        } else {
          // eslint-disable-next-line no-console
          console.error("Final submission failed:", (result && result.Message) || "Unknown error");
        }
      } else {
        // eslint-disable-next-line no-console
        console.error("Final submission failed:", response.status, response.statusText);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      if (error && error.name === "AbortError") {
        console.error("Final submit request timed out (>90s). Backend slow or CORS preflight blocked.");
      } else if (error instanceof TypeError) {
        console.error("Final submit network/CORS error — check browser network tab:", error);
      } else {
        console.error("Error submitting final order:", error);
      }
    } finally {
      setIsMakingAPIRequest(false);
    }
  };

  const handleFieldBlur = e => {
    const field = e.target;
    const fieldWrapper = field.closest(".field-wrapper");
    if (!fieldWrapper) return;

    // Remove existing error class, only validate on submit
    if (e.target.value !== "") fieldWrapper.classList.remove("has-error");
  };

  const stateDropdown = (id, name) => (
    <select
      id={id}
      name={name}
      data-required="true"
      onBlur={handleFieldBlur}
      onChange={handleFieldBlur}
    >
      <option value="" disabled selected>
        Select State
      </option>
      <option value="AL">Alabama</option>
      <option value="AK">Alaska</option>
      <option value="AZ">Arizona</option>
      <option value="AR">Arkansas</option>
      <option value="CA">California</option>
      <option value="CO">Colorado</option>
      <option value="CT">Connecticut</option>
      <option value="DE">Delaware</option>
      <option value="DC">District of Columbia</option>
      <option value="FL">Florida</option>
      <option value="GA">Georgia</option>
      <option value="GU">Guam</option>
      <option value="HI">Hawaii</option>
      <option value="ID">Idaho</option>
      <option value="IL">Illinois</option>
      <option value="IN">Indiana</option>
      <option value="IA">Iowa</option>
      <option value="KS">Kansas</option>
      <option value="KY">Kentucky</option>
      <option value="LA">Louisiana</option>
      <option value="ME">Maine</option>
      <option value="MD">Maryland</option>
      <option value="MA">Massachusetts</option>
      <option value="MI">Michigan</option>
      <option value="MN">Minnesota</option>
      <option value="MS">Mississippi</option>
      <option value="MO">Missouri</option>
      <option value="MT">Montana</option>
      <option value="NE">Nebraska</option>
      <option value="NV">Nevada</option>
      <option value="NH">New Hampshire</option>
      <option value="NJ">New Jersey</option>
      <option value="NM">New Mexico</option>
      <option value="NY">New York</option>
      <option value="NC">North Carolina</option>
      <option value="ND">North Dakota</option>
      <option value="OH">Ohio</option>
      <option value="OK">Oklahoma</option>
      <option value="OR">Oregon</option>
      <option value="PA">Pennsylvania</option>
      <option value="PR">Puerto Rico</option>
      <option value="RI">Rhode Island</option>
      <option value="MP">Saipan</option>
      <option value="SC">South Carolina</option>
      <option value="SD">South Dakota</option>
      <option value="TN">Tennessee</option>
      <option value="TX">Texas</option>
      <option value="UT">Utah</option>
      <option value="VT">Vermont</option>
      <option value="VI">Virgin Islands</option>
      <option value="VA">Virginia</option>
      <option value="WA">Washington</option>
      <option value="WV">West Virginia</option>
      <option value="WI">Wisconsin</option>
      <option value="WY">Wyoming</option>
    </select>
  );

  const renderIssuingOffice = () => (
    <fieldset className="form-fieldset-wrapper gray-bg col-12">
      <legend htmlFor="cplRequest" className="fieldset-label">
        CPL Request - Stewart Title
      </legend>

      <div className="field-wrapper col-6">
        <label className="sc-form-item__label" htmlFor="userEmail">
          Your Email
        </label>
        <input
          type="email"
          name="userEmail"
          id="userEmail"
          data-required="true"
          onBlur={handleFieldBlur}
        />
        <span role="alert" className="sc-form-item__error-msg">
          <span className="field-validation-error">
            <span className="">Email is a required field</span>
          </span>
        </span>
      </div>

      <div className="field-wrapper col-6">
        <label className="sc-form-item__label" htmlFor="orderId">
          Title Order Number
        </label>
        <input
          type="text"
          name="orderId"
          id="orderId"
          data-required="true"
          onBlur={handleFieldBlur}
        />
        <span role="alert" className="sc-form-item__error-msg">
          <span className="field-validation-error">
            <span className="">Title Order Number is a required field</span>
          </span>
        </span>
      </div>
      <div className="field-wrapper col-6">
        <label htmlFor="issuingOffice" className="sc-form-item__label">
          Issuing Office
        </label>
        <select
          name="issuingOffice"
          id="issuingOffice"
          data-required="true"
          onBlur={handleFieldBlur}
          onChange={handleFieldBlur}
        >
          <option selected disabled value="">
            Select an Issuing Office
          </option>
          <option value="Stewart Title of California, Inc. - 11870 Pierce Street, Suite 100, Riverside, CA, 92505">
            Stewart Title of California, Inc. - 11870 Pierce Street, Suite 100,
            Riverside, CA, 92505
          </option>
          <option value="Stewart Title of California, Inc. - 1180 Iron Point Rd., Suite 125, Folsom, CA, 95630">
            Stewart Title of California, Inc. - 1180 Iron Point Rd., Suite 125,
            Folsom, CA, 95630
          </option>
          <option value="Stewart Title of California, Inc. - 1200 California Street, Suite 120, Redlands, CA, 92374">
            Stewart Title of California, Inc. - 1200 California Street, Suite
            120, Redlands, CA, 92374
          </option>
          <option value="Stewart Title of California, Inc. - 12550 Hesperia Road, Suite 200, Victorville, CA, 92395">
            Stewart Title of California, Inc. - 12550 Hesperia Road, Suite 200,
            Victorville, CA, 92395
          </option>
          <option value="Stewart Title of California, Inc. - 13181 Crossroads Parkway North, Suite 140, City of Industry, CA, 91746">
            Stewart Title of California, Inc. - 13181 Crossroads Parkway North,
            Suite 140, City of Industry, CA, 91746
          </option>
          <option value="Stewart Title of California, Inc. - 1541 D Pacific Avenue, Santa Cruz, CA, 95060">
            Stewart Title of California, Inc. - 1541 D Pacific Avenue, Santa
            Cruz, CA, 95060
          </option>
          <option value="Stewart Title of California, Inc. - 1777 Botelho Dr., Suite 120, Walnut Creek, CA, 94596">
            Stewart Title of California, Inc. - 1777 Botelho Dr., Suite 120,
            Walnut Creek, CA, 94596
          </option>
          <option value="Stewart Title of California, Inc. - 1900 Grass Valley Hwy, Suite 50, Auburn, CA, 95603">
            Stewart Title of California, Inc. - 1900 Grass Valley Hwy, Suite 50,
            Auburn, CA, 95603
          </option>
          <option value="Stewart Title of California, Inc. - 2030 W. Monte Vista Avenue, Turlock, CA, 95382">
            Stewart Title of California, Inc. - 2030 W. Monte Vista Avenue,
            Turlock, CA, 95382
          </option>
          <option value="Stewart Title of California, Inc. - 2240 Douglas Blvd., Suite 180, Roseville, CA, 95661">
            Stewart Title of California, Inc. - 2240 Douglas Blvd., Suite 180,
            Roseville, CA, 95661
          </option>
          <option value="Stewart Title of California, Inc. - 2801 Townsgate Road, Suite 111, Westlake Village, CA, 91361">
            Stewart Title of California, Inc. - 2801 Townsgate Road, Suite 111,
            Westlake Village, CA, 91361
          </option>
          <option value="Stewart Title of California, Inc. - 2850 Cordelia Rd., Suite 200, Fairfield, CA, 94534">
            Stewart Title of California, Inc. - 2850 Cordelia Rd., Suite 200,
            Fairfield, CA, 94534
          </option>
          <option value="Stewart Title of California, Inc. - 296 West Henderson Ave., Porterville, CA, 93257">
            Stewart Title of California, Inc. - 296 West Henderson Ave.,
            Porterville, CA, 93257
          </option>
          <option value="Stewart Title of California, Inc. - 3031 Tisch Way, Suite 230, San Jose, CA, 95128">
            Stewart Title of California, Inc. - 3031 Tisch Way, Suite 230, San
            Jose, CA, 95128
          </option>
          <option value="Stewart Title of California, Inc. - 325 Mall Drive, Suite 105, Hanford, CA, 93230">
            Stewart Title of California, Inc. - 325 Mall Drive, Suite 105,
            Hanford, CA, 93230
          </option>
          <option value="Stewart Title of California, Inc. - 3461 Fair Oaks Blvd., Suite 150, Sacramento, CA, 95864">
            Stewart Title of California, Inc. - 3461 Fair Oaks Blvd., Suite 150,
            Sacramento, CA, 95864
          </option>
          <option value="Stewart Title of California, Inc. - 3740 W. Mineral King Ave., Visalia, CA, 93291">
            Stewart Title of California, Inc. - 3740 W. Mineral King Ave.,
            Visalia, CA, 93291
          </option>
          <option value="Stewart Title of California, Inc. - 401 Primrose Rd., Suite F, Burlingame, CA, 94010">
            Stewart Title of California, Inc. - 401 Primrose Rd., Suite F,
            Burlingame, CA, 94010
          </option>
          <option value="Stewart Title of California, Inc. - 409 Washington Street, Ste. 202A, Monterey, CA, 93940">
            Stewart Title of California, Inc. - 409 Washington Street, Ste.
            202A, Monterey, CA, 93940
          </option>
          <option value="Stewart Title of California, Inc. - 4126 24th Street, San Francisco, CA, 94114">
            Stewart Title of California, Inc. - 4126 24th Street, San Francisco,
            CA, 94114
          </option>
          <option value="Stewart Title of California, Inc. - 41391 Kalmia Street, Suite 330, Murrieta, CA, 92562">
            Stewart Title of California, Inc. - 41391 Kalmia Street, Suite 330,
            Murrieta, CA, 92562
          </option>
          <option value="Stewart Title of California, Inc. - 42455 10th Street West, Suite 101, Lancaster, CA, 93534">
            Stewart Title of California, Inc. - 42455 10th Street West, Suite
            101, Lancaster, CA, 93534
          </option>
          <option value="Stewart Title of California, Inc. - 5291 California Ave., Suite 300, Irvine, CA, 92617">
            Stewart Title of California, Inc. - 5291 California Ave., Suite 300,
            Irvine, CA, 92617
          </option>
          <option value="Stewart Title of California, Inc. - 548 Gibson Dr., Suite 170, Roseville, CA, 95678">
            Stewart Title of California, Inc. - 548 Gibson Dr., Suite 170,
            Roseville, CA, 95678
          </option>
          <option value="Stewart Title of California, Inc. - 555 Capitol Mall, Suite 545, Sacramento, CA, 95814">
            Stewart Title of California, Inc. - 555 Capitol Mall, Suite 545,
            Sacramento, CA, 95814
          </option>
          <option value="Stewart Title of California, Inc. - 5729 Sunrise Blvd., Citrus Heights, CA, 95610">
            Stewart Title of California, Inc. - 5729 Sunrise Blvd., Citrus
            Heights, CA, 95610
          </option>
          <option value="Stewart Title of California, Inc. - 5740 Ralston Street, Suite 301, Ventura, CA, 93003">
            Stewart Title of California, Inc. - 5740 Ralston Street, Suite 301,
            Ventura, CA, 93003
          </option>
          <option value="Stewart Title of California, Inc. - 6700 Fair Oaks Blvd., Suite B, Carmichael, CA, 95608">
            Stewart Title of California, Inc. - 6700 Fair Oaks Blvd., Suite B,
            Carmichael, CA, 95608
          </option>
          <option value="Stewart Title of California, Inc. - 73-020 El Paseo, Suite 103, Palm Desert, CA, 92260">
            Stewart Title of California, Inc. - 73-020 El Paseo, Suite 103, Palm
            Desert, CA, 92260
          </option>
          <option value="Stewart Title of California, Inc. - 730 Alhambra Blvd., Suite 202, Sacramento, CA, 95816">
            Stewart Title of California, Inc. - 730 Alhambra Blvd., Suite 202,
            Sacramento, CA, 95816
          </option>
          <option value="Stewart Title of California, Inc. - 7480 North Palm Avenue, Suite 103, Fresno, CA, 93711">
            Stewart Title of California, Inc. - 7480 North Palm Avenue, Suite
            103, Fresno, CA, 93711
          </option>
          <option value="Stewart Title of California, Inc. - 750 University Ave., Suite 120, Los Gatos, CA, 95032">
            Stewart Title of California, Inc. - 750 University Ave., Suite 120,
            Los Gatos, CA, 95032
          </option>
          <option value="Stewart Title of California, Inc. - 760 West Main Street, El Centro, CA, 92243">
            Stewart Title of California, Inc. - 760 West Main Street, El Centro,
            CA, 92243
          </option>
          <option value="Stewart Title of California, Inc. - 7676 Hazard Center Drive, Suite 1400, San Diego, CA, 92108">
            Stewart Title of California, Inc. - 7676 Hazard Center Drive, Suite
            1400, San Diego, CA, 92108
          </option>
          <option value="Stewart Title of California, Inc. - 77 Lincoln Blvd., Suite 2, Lincoln, CA, 95648">
            Stewart Title of California, Inc. - 77 Lincoln Blvd., Suite 2,
            Lincoln, CA, 95648
          </option>
          <option value="Stewart Title of California, Inc. - 800 N. Brand Blvd, 9th Floor, Glendale, CA, 91203">
            Stewart Title of California, Inc. - 800 N. Brand Blvd, 9th Floor,
            Glendale, CA, 91203
          </option>
          <option value="Stewart Title of California, Inc. - 801 Traeger Ave., Ste 103, San Bruno, CA, 94066">
            Stewart Title of California, Inc. - 801 Traeger Ave., Ste 103, San
            Bruno, CA, 94066
          </option>
          <option value="Stewart Title of California, Inc. - 809 Bay Avenue, Suite D, Capitola, CA, 95010">
            Stewart Title of California, Inc. - 809 Bay Avenue, Suite D,
            Capitola, CA, 95010
          </option>
          <option value="Stewart Title of California, Inc. - 901 Campisi Way, Suite 100, Campbell, CA, 95008">
            Stewart Title of California, Inc. - 901 Campisi Way, Suite 100,
            Campbell, CA, 95008
          </option>
          <option value="Stewart Title of California, Inc. - 9250 Laguna Springs Dr., Suite 117, Elk Grove, CA, 95758">
            Stewart Title of California, Inc. - 9250 Laguna Springs Dr., Suite
            117, Elk Grove, CA, 95758
          </option>
        </select>
        <span role="alert" className="sc-form-item__error-msg">
          <span className="field-validation-error">
            <span>Issuing Office is a required field</span>
          </span>
        </span>
      </div>
    </fieldset>
  );

  const renderLoanLender = (isSecondary = false) => (
    <fieldset className="form-fieldset-wrapper gray-bg col-12">
      {isSecondary && (
        <div className="col-6">
          <input
            type="checkbox"
            onChange={() =>
              setShowSecondaryLoanLender(!showSecondaryLoanLender)
            }
          />{" "}
          Add Secondary Lender
        </div>
      )}
      {(!isSecondary || (isSecondary && showSecondaryLoanLender)) && (
        <>
          <legend htmlFor="cplRequest" className="fieldset-label">
            {isSecondary ? "Secondary " : ""}Loan / Lender Information
          </legend>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label "
              htmlFor={`${isSecondary ? "secondary" : "primary"}LenderLoanNumber`}
            >
              Loan Number
            </label>
            <input
              type="text"
              name={`${isSecondary ? "secondary" : "primary"}LenderLoanNumber`}
              id={`${isSecondary ? "secondary" : "primary"}LenderLoanNumber`}
              data-required="true"
              onBlur={handleFieldBlur}
            />
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Loan Number is a required field</span>
              </span>
            </span>
          </div>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}LenderName`}
            >
              Lender Name
            </label>
            <input
              type="text"
              name={`${isSecondary ? "secondary" : "primary"}LenderName`}
              id={`${isSecondary ? "secondary" : "primary"}LenderName`}
              data-required="true"
              onBlur={handleFieldBlur}
            />
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Lender Name is a required field</span>
              </span>
            </span>
          </div>
          <div className="field-wrapper col-12">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}LenderDesignation`}
            >
              Lender Name Designation: (if applicable)
            </label>
            <select
              name={`${isSecondary ? "secondary" : "primary"}LenderDesignation`}
              id={`${isSecondary ? "secondary" : "primary"}LenderDesignation`}
            >
              <option value="" selected disabled>
                Select a Lender Name Designation
              </option>
              <option value="secretary-housing-and-urban-development">
                and/or Secretary of Housing and Urban Development, its
                Respective Successors and/or Assigns as their interests may
                appear
              </option>
              <option value="its-respective-successors">
                its Respective Successors and/or Assigns as their interests may
                appear
              </option>
              <option value="its-successors">
                its Successors and/or Assigns as their respective interests may
                appear
              </option>
              <option value="secretary-of-veterans-affairs">
                and/or Secretary of Veterans Affairs and/or its Respective
                Successors and Assigns as their interests my appear
              </option>
              <option value="secretary-of-veteran-officer">
                and/or The Secretary of Veteran Affairs, an Officer of the
                United States of America, their Successors and Assigns, as their
                interest may appear
              </option>
              <option value="its-sucessors-assigns">
                its Successors and/or Assigns as their interests may appear
              </option>
              <option value="its-successors-and-or-assigns">
                its Successors and/or Assigns
              </option>
              <option value="isaoa-atima">ISAOA/ATIMA</option>
              <option value="isaoa">ISAOA</option>
            </select>
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-valid">
                Lender Name Designation is a required field
              </span>
            </span>
          </div>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}LenderAddress`}
            >
              Lender Address
            </label>
            <input
              type="text"
              name={`${isSecondary ? "secondary" : "primary"}LenderAddress`}
              id={`${isSecondary ? "secondary" : "primary"}LenderAddress`}
              data-required="true"
              onBlur={handleFieldBlur}
            />
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Lender Address is a required field</span>
              </span>
            </span>
          </div>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}LenderCity`}
            >
              Lender City
            </label>
            <input
              type="text"
              name={`${isSecondary ? "secondary" : "primary"}LenderCity`}
              id={`${isSecondary ? "secondary" : "primary"}LenderCity`}
              data-required="true"
              onBlur={handleFieldBlur}
            />
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Lender City is a required field</span>
              </span>
            </span>
          </div>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}LenderStateCode`}
            >
              Lender State
            </label>
            {stateDropdown(
              `${isSecondary ? "secondary" : "primary"}LenderStateCode`,
              `${isSecondary ? "secondary" : "primary"}LenderStateCode`,
            )}
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-valid">
                Lender State is a required field
              </span>
            </span>
          </div>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}LenderZip`}
            >
              Lender ZIP
            </label>
            <input
              type="text"
              name={`${isSecondary ? "secondary" : "primary"}LenderZip`}
              id={`${isSecondary ? "secondary" : "primary"}LenderZip`}
              data-required="true"
              onBlur={handleFieldBlur}
            />
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Lender ZIP is a required field</span>
              </span>
            </span>
          </div>
        </>
      )}
    </fieldset>
  );

  const renderPropertyBorrower = (isSecondary = false) => (
    <fieldset className="form-fieldset-wrapper gray-bg col-12">
      {isSecondary && (
        <div className="col-6">
          <input
            type="checkbox"
            onChange={() =>
              setShowSecondaryPropertyBorrower(!showSecondaryPropertyBorrower)
            }
          />{" "}
          Add Secondary Borrower
        </div>
      )}
      {(!isSecondary || (isSecondary && showSecondaryPropertyBorrower)) && (
        <>
          <legend htmlFor="cplRequest" className="fieldset-label">
            {isSecondary ? "Secondary " : ""}Property / Borrower Information
          </legend>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}BorrowerFullName`}
            >
              Borrower Name(s)
            </label>
            <input
              type="text"
              name={`${isSecondary ? "secondary" : "primary"}BorrowerFullName`}
              id={`${isSecondary ? "secondary" : "primary"}BorrowerFullName`}
              data-required="true"
              onBlur={handleFieldBlur}
            />
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Borrower Name(s) is a required field</span>
              </span>
            </span>
          </div>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}BorrowerAddress`}
            >
              Property Address
            </label>
            <input
              type="text"
              name={`${isSecondary ? "secondary" : "primary"}BorrowerAddress`}
              id={`${isSecondary ? "secondary" : "primary"}BorrowerAddress`}
              data-required="true"
              onBlur={handleFieldBlur}
            />
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Property Address is a required field</span>
              </span>
            </span>
          </div>

          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}BorrowerCity`}
            >
              Property City
            </label>
            <input
              type="text"
              id={`${isSecondary ? "secondary" : "primary"}BorrowerCity`}
              name={`${isSecondary ? "secondary" : "primary"}BorrowerCity`}
              data-required="true"
              onBlur={handleFieldBlur}
            />
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Property City is a required field</span>
              </span>
            </span>
          </div>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}BorrowerStateCode`}
            >
              Property State
            </label>
            <select
              id={`${isSecondary ? "secondary" : "primary"}BorrowerStateCode`}
              name={`${isSecondary ? "secondary" : "primary"}BorrowerStateCode`}
              data-required="true"
              onBlur={handleFieldBlur}
              onChange={handleFieldBlur}
            >
              <option value="CA">California</option>
            </select>
            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-valid">
                Property State is a required field
              </span>
            </span>
          </div>
          <div className="field-wrapper col-6">
            <label
              className="sc-form-item__label"
              htmlFor={`${isSecondary ? "secondary" : "primary"}BorrowerZip`}
            >
              Property ZIP
            </label>
            <input
              type="text"
              id={`${isSecondary ? "secondary" : "primary"}BorrowerZip`}
              name={`${isSecondary ? "secondary" : "primary"}BorrowerZip`}
              data-required="true"
              onBlur={handleFieldBlur}
            />

            <span role="alert" className="sc-form-item__error-msg">
              <span className="field-validation-error">
                <span className="">Property ZIP is a required field</span>
              </span>
            </span>
          </div>
          {!isSecondary && (
            <div className="field-wrapper col-6">
              <label
                className="sc-form-item__label"
                htmlFor="PrimaryBorrowerCountyName"
              >
                Property County
              </label>
              <select
                id="PrimaryBorrowerCountyName"
                name="PrimaryBorrowerCountyName"
                data-required="true"
                onBlur={handleFieldBlur}
                onChange={handleFieldBlur}
                disabled={isLoadingCounties}
                defaultValue=""
              >
                <option value="" disabled>
                  {isLoadingCounties ? "Loading counties..." : "Select County"}
                </option>
                {counties.map(county => (
                  <option key={county} value={county}>
                    {county}
                  </option>
                ))}
              </select>
              <span role="alert" className="sc-form-item__error-msg">
                <span className="field-validation-error">
                  <span className="">Property County is a required field</span>
                </span>
              </span>
            </div>
          )}
        </>
      )}
    </fieldset>
  );

  return (
    <>
      {formSubmitted && readyToFinalize && (
        <div className="migrated-form">
          <fieldset className="form-fieldset-wrapper gray-bg col-12">
            <h3 className="col-12">Thank you!</h3>
            <p className="col-12">
              CPL has been generated and emailed to email
            </p>
            <p className="col-12">
              You can also view the CPL by clicking the button below:
            </p>
            <div className="cpl-form__actions col-12">
              <button
                className="btn btn--primary btn--primary--inverted"
                id="openpdf"
                onClick={previewPDF}
              >
                Open PDF
              </button>
              <button
                className="btn btn--primary"
                onClick={() => window.location.reload()}
              >
                Start New Form
              </button>
            </div>
          </fieldset>
        </div>
      )}
      {!formSubmitted && (
        <form
          className="migrated-form cpl-form"
          ref={cplFormRef}
          onSubmit={handlePreviewSubmit}
        >
          <fieldset className="form-fieldset-wrapper gray-bg col-12">
            <fieldset className="form-fieldset-wrapper gray-bg col-12">
              <legend htmlFor="closingProtection" className="fieldset-label">
                Closing Protection Letter
              </legend>
              <div className="field-wrapper col-6">
                <label className="sc-form-item__label" htmlFor="cplDate">
                  Date
                </label>
                <input
                  type="date"
                  name="cplDate"
                  id="cplDate"
                  data-required="true"
                  onBlur={handleFieldBlur}
                />
                <span role="alert" className="sc-form-item__error-msg">
                  <span className="field-validation-error">
                    <span className="">Date is a required field</span>
                  </span>
                </span>
              </div>
            </fieldset>
            {renderIssuingOffice()}
            {renderLoanLender(false)}
            {renderLoanLender(true)}
            {renderPropertyBorrower(false)}
            {renderPropertyBorrower(true)}
            {!readyToFinalize && (
              <div className="cpl-form__actions col-12">
                <button
                  className="btn btn--primary"
                  type="submit"
                  disabled={isMakingAPIRequest}
                >
                  Preview
                </button>
                {isMakingAPIRequest && <>Submitting...</>}
              </div>
            )}
            {readyToFinalize && (
              <div className="cpl-form__actions col-12">
                <button
                  className="btn btn--primary"
                  type="button"
                  onClick={previewPDF}
                >
                  Open PDF
                </button>
                <strong className="col-12">
                  Please make sure you have previewed the document. Proceed with
                  submit?
                </strong>
                <button
                  className="btn btn--primary"
                  type="button"
                  onClick={handleFinalSubmit}
                  disabled={isMakingAPIRequest}
                >
                  Yes
                </button>
                <button
                  className="btn btn--primary btn--primary--inverted"
                  type="button"
                  onClick={() => {
                    setPdfDoc(null);
                    setReadyToFinalize(false);
                    setTpsProductUniqueID(null);
                  }}
                >
                  No
                </button>
              </div>
            )}
          </fieldset>
        </form>
      )}
    </>
  );
};

CPLForm.propTypes = propTypes;
export default CPLForm;
